"use client";

import { useState } from "react";
import Link from "next/link";
import toast from "react-hot-toast";
import { PopupShell } from "@/app/components/appcomponents/thankYouWizard/parts";
import orderService from "@/services/order-service";
import ObituaryPicker from "./ObituaryPicker";
import SampleObituaryCard from "./SampleObituaryCard";
import { PrimaryButton } from "./PopupButtons";
import { PRICES, euro } from "./orderConfig";
import { useAuth } from "@/hooks/useAuth";

/**
 * "Poudarjena osmrtnica": the visitor picks the obituary right here (so no Step 0), pays at Stripe, and
 * the obituary then gets its own colours and is listed first until the funeral.
 */
export default function HighlightOrderModal({ obituary: initial = null, onClose }) {
  const { isAuthenticated } = useAuth();
  const [obituary, setObituary] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [needLogin, setNeedLogin] = useState(false);

  // Anybody can open the popup to see what a highlighted obituary is; the login is asked only when ordering
  // (after the obituary is chosen, before Stripe).
  if (needLogin && !isAuthenticated) {
    return (
      <PopupShell onClose={onClose} panelTitle="Poudarjena osmrtnica" compact flatOnPhone closeOnBackdrop={false}>
        <p className="text-[16px] leading-[24px] text-[#1E2125]">
          Za naročilo poudarjene osmrtnice se moraš prijaviti.
        </p>
        <div className="mx-auto mt-[28px] w-[250px] mobile:w-full">
          <Link
            href="/registracija"
            className="flex h-[44px] w-full items-center justify-center rounded-[4px] bg-gradient-to-b from-[#0D94E8] to-[#0A85C2] text-[16px] text-white shadow-custom-dark-bottom"
          >
            Prijava
          </Link>
        </div>
      </PopupShell>
    );
  }

  const submit = async () => {
    if (busy) return;
    if (!obituary) {
      toast.error("Izberi osmrtnico s seznama");
      return;
    }
    if (!isAuthenticated) {
      setNeedLogin(true);
      return;
    }
    setBusy(true);
    try {
      const { paymentUrl } = await orderService.createCheckout({
        product: "highlight",
        obituaryId: obituary.id,
      });
      window.location.href = paymentUrl;
    } catch (error) {
      toast.error(error?.message || "Prišlo je do napake. Poskusi znova.");
      setBusy(false);
    }
  };

  return (
    <PopupShell onClose={onClose} panelTitle="Poudarjena osmrtnica" compact flatOnPhone closeOnBackdrop={false}>
      <span className="inline-block rounded-full bg-[#EDE7FF] px-2 py-[1px] text-[10px] leading-[14px] text-[#7B5CF0]">
        Izstopa
      </span>
      <h2 className="flex items-center gap-2 text-[24px] leading-[28px] text-[#14105F] mobile:text-[18px]">
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" className="shrink-0">
          <circle cx="8" cy="8" r="7" fill="none" stroke="#E5242B" strokeWidth="1.5" />
          <text x="8" y="11.5" textAnchor="middle" fontSize="10" fontWeight="700" fill="#E5242B">?</text>
        </svg>
        Poudarjena osmrtnica
      </h2>
      <p className="mt-2 text-[15px] leading-[22px] text-[#6D778E] mobile:text-[14px] mobile:leading-[20px]">
        Osmrtnica izstopa od drugih; ima obarvano ozadje, poudarjen okvir druge barve in je do pogreba
        vedno na zgornjem mestu.
      </p>

      <div className="mt-6 flex flex-col gap-5 mobile:mt-5 mobile:gap-3">
        <SampleObituaryCard obituary={obituary} highlighted />
        <SampleObituaryCard obituary={obituary} />
      </div>

      {/* narrower than the cards on a tablet / desktop: it looks lighter and clearly apart from them */}
      <div className="mx-auto mt-8 w-[82%] mobile:mt-6 mobile:w-full">
        <ObituaryPicker value={obituary} onChange={setObituary} placeholder="Poišči osmrtnico" />
      </div>

      <div className="mt-6 flex justify-center">
        <PrimaryButton onClick={submit} loading={busy} width="w-[226px]">
          {`Naročilo – ${euro(PRICES.highlight)}`}
        </PrimaryButton>
      </div>
    </PopupShell>
  );
}
